import { FC } from 'react'
import InlineSvg from 'react-inlinesvg'
import { observer } from 'mobx-react-lite'
import { GatsbyImage, getImage } from 'gatsby-plugin-image'
import { JSXMapSerializer, PrismicRichText } from '@prismicio/react'
import { ImageField, LinkField } from '@prismicio/client'
import tw from 'twin.macro'
import { Grid, LargeHeader, SubHeader, SmallBody } from '../UI'
import PrismicLinkInternalOrExternal from '../PrismicLinkInternalOrExternal'
import { renderStrikethrough } from '../../utils/renderStrikethrough'
import { RichTextFields } from '../../types/prismic'

import logo from '../../images/gabbi-logo-white-r.png'
import ctaArrow from '../../images/icons/cta-arrow.svg'
import { richTextStrikethroughComponent } from '../../utils/richTextComponents'

const richTextBoldMagentaComponent: JSXMapSerializer = {
  strong: ({ children }) => (
    <strong tw="uppercase font-object line-height[1] text-gb-magenta">
      {children}
    </strong>
  ),
}

type Props = {
  header: RichTextFields
  subhead: RichTextFields
  image: ImageField
  partnerLogo: ImageField
  cta: {
    text?: string
    link?: LinkField
    text2?: string
    link2?: LinkField
    subtext?: string
  }
  headerRichTextComponents?: JSXMapSerializer
  pagePath: string
}

const LeadSectionHero: FC<Props> = ({
  header,
  subhead,
  image,
  partnerLogo,
  cta,
  headerRichTextComponents = richTextBoldMagentaComponent,
  pagePath,
}) => {
  const hasPartnerLogo = !!partnerLogo && !!partnerLogo.url

  return (
    <section tw="w-full h-screen relative font-domaine">
      {/* Background */}
      <GatsbyImage
        image={getImage(image)}
        tw="absolute top-0 left-0 w-full h-full object-cover z-[-1]"
        objectPosition="center top"
        alt=""
      />
      {/* Content */}
      <div tw="h-full flex items-end">
        <Grid tw="h-[calc(100vh - 109px)] lg:h-[calc(100vh - 126px)]">
          <div tw="flex flex-col justify-center col-span-4 lg:(col-start-3 col-span-8)">
            {hasPartnerLogo && (
              <div tw="flex flex-row justify-center items-center mb-16 lg:mb-12">
                <img src={logo} tw="w-[185px] lg:w-[204px]" alt="" />
                <p tw="text-[36px] font-sans font-normal text-white mx-4 lg:(text-[48px] mx-8)">
                  X
                </p>
                <GatsbyImage
                  image={getImage(partnerLogo)}
                  tw="max-w-[320px] max-h-[200px]"
                  imgStyle={{ objectFit: 'contain' }}
                  alt=""
                />
              </div>
            )}
            <h1 css={[LargeHeader, tw`w-full text-white text-center lg:px-6`]}>
              <PrismicRichText
                field={header.richText}
                components={headerRichTextComponents}
              />
            </h1>
            <hr tw="border-white w-[57px] mt-[37px] mb-[41px] lg:(mt-[18px] mb-[26px]) mx-auto" />
            <h2
              css={[
                SubHeader,
                tw`text-center text-white whitespace-pre-line mb-8 lg:px-24`,
              ]}
            >
              <PrismicRichText
                field={subhead.richText}
                components={richTextStrikethroughComponent}
              />
            </h2>
            {/* CTA Buttons */}
            {cta.text && cta.link && (
              <PrismicLinkInternalOrExternal
                field={cta.link}
                trackEventParams={{
                  pageTitle: header.text,
                  path: pagePath,
                  target: 'url' in cta.link ? cta.link.url : '',
                  copy: cta.text,
                }}
                cssStyle={tw`bg-gb-red text-white uppercase px-12 py-4 w-full font-object text-[13px] line-height[13px] letter-spacing[1px] mx-auto flex justify-center items-center sm:max-w-[350px] transition-colors hover:bg-gb-red-dark`}
              >
                {cta.text}
                <InlineSvg
                  src={ctaArrow}
                  tw="w-6 ml-[5px] mt-[-2px] inline text-white"
                />
              </PrismicLinkInternalOrExternal>
            )}
            {cta.text2 && cta.link2 && (
              <PrismicLinkInternalOrExternal
                field={cta.link2}
                trackEventParams={{
                  pageTitle: header.text,
                  path: pagePath,
                  target: 'url' in cta.link2 ? cta.link2.url : '',
                  copy: cta.text2,
                }}
                cssStyle={tw`bg-gb-blue text-white uppercase px-12 py-4 w-full font-object text-[13px] line-height[13px] letter-spacing[1px] mx-auto flex justify-center items-center sm:max-w-[350px] transition-colors hover:bg-gb-blue-dark mt-4`}
              >
                {cta.text2}
                <InlineSvg
                  src={ctaArrow}
                  tw="w-6 ml-[5px] mt-[-2px] inline text-white"
                />
              </PrismicLinkInternalOrExternal>
            )}
            {!!cta.subtext && (
              <div
                css={[SmallBody, tw`col-span-full mt-5 text-center text-white`]}
              >
                {cta.subtext}
              </div>
            )}
          </div>
        </Grid>
      </div>
    </section>
  )
}

export default observer(LeadSectionHero)
